import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Calendar, Wrench } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { projects } from "@/data/projects";
import PageHeader from "@/components/layout/PageHeader";
import Reveal from "@/components/motion/Reveal";
import CtaBand from "@/components/layout/CtaBand";

const Projects = () => {
  const { t, lang } = useLanguage();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const visible = filter === "all" ? projects : projects.filter((p) => p.categoryKey === filter);

  const categoryLabel = (key) => t.projectsSection.filters.find((f) => f.key === key)?.label || key;

  return (
    <>
      <PageHeader chapter={t.projectsPage.chapter} title={t.projectsPage.title} subtitle={t.projectsPage.subtitle} testid="projects-header" />

      <section data-testid="projects-gallery" className="bg-ink py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtres réalisations">
              {t.projectsSection.filters.map((f) => (
                <button
                  key={f.key}
                  role="tab"
                  aria-selected={filter === f.key}
                  data-testid={`portfolio-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                    filter === f.key
                      ? "border-brand bg-brand text-ink"
                      : "border-line text-fog hover:border-brand hover:text-brand"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </Reveal>

          <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence mode="popLayout">
              {visible.map((p) => (
                <motion.article
                  layout
                  key={p.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.35 }}
                  data-testid={`project-card-${p.id}`}
                  className="group cursor-pointer border border-line bg-panel"
                  onClick={() => setSelected(p)}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title[lang]}
                      loading="lazy"
                      className="h-full w-full object-cover transition-all duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-panel via-transparent to-transparent" aria-hidden="true" />
                    <span className="absolute left-4 top-4 bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-brand backdrop-blur">
                      {categoryLabel(p.categoryKey)}
                    </span>
                  </div>
                  <div className="p-5">
                    <h2 className="font-display text-xl font-bold uppercase leading-tight text-cream">{p.title[lang]}</h2>
                    <p className="mt-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-fog">
                      <MapPin className="h-3.5 w-3.5 text-brand" /> {p.location}
                    </p>
                    <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-ash group-hover:text-brand transition-colors">
                      {t.projectsSection.viewProject} →
                    </p>
                  </div>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>

          <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-ash" data-testid="projects-demo-note">
            * {t.projectsSection.demoNote}
          </p>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="project-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink/85 p-0 sm:p-6 backdrop-blur-sm"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 220, damping: 26 }}
              className="relative max-h-[92vh] w-full max-w-3xl overflow-y-auto border border-line bg-surface"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="project-modal-close"
                aria-label={t.projectsSection.close}
                onClick={() => setSelected(null)}
                className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center bg-ink/80 text-cream backdrop-blur transition-colors hover:bg-brand hover:text-ink"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={selected.image} alt={selected.title[lang]} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" aria-hidden="true" />
              </div>
              <div className="p-6 sm:p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{categoryLabel(selected.categoryKey)}</p>
                <h2 className="mt-3 font-display text-3xl font-bold uppercase leading-tight text-cream">{selected.title[lang]}</h2>
                <div className="mt-5 grid gap-4 sm:grid-cols-3 border-y border-line py-4">
                  <div>
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                      <MapPin className="h-3.5 w-3.5 text-brand" /> {t.projectsSection.location}
                    </p>
                    <p className="mt-1.5 text-sm text-cream">{selected.location}</p>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                      <Wrench className="h-3.5 w-3.5 text-brand" /> {t.projectsSection.type}
                    </p>
                    <p className="mt-1.5 text-sm text-cream">{selected.type[lang]}</p>
                  </div>
                  <div>
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-ash">
                      <Calendar className="h-3.5 w-3.5 text-brand" /> {t.projectsSection.year}
                    </p>
                    <p className="mt-1.5 text-sm text-cream">{selected.year}</p>
                  </div>
                </div>
                <p className="mt-5 text-sm sm:text-base leading-relaxed text-fog">{selected.description[lang]}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <CtaBand />
    </>
  );
};

export default Projects;
